/**
 * Generated by orval 🍺
 * Do not edit manually.
 * PASS API
 * OpenAPI spec version: 3.0.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";

import { nmibleInstanceV3 } from "../../mutator/axios-instance-v3";

import type {
  DataResponseListMicropaymentConfigurationInfo,
  DataResponseListMicropaymentConfigurationWithMicropayments,
  DataResponseMicropaymentConfirmationRead,
  ErrorResponse,
  GetMicropaymentConfigurationMicropaymentsConfigGetParams,
  GetMicropaymentValueConfigMicropaymentsValuesGetParams,
  HTTPValidationError,
  MicropaymentAction,
  MicropaymentConfigurationSyncRequest,
  MicropaymentConfirmationRequest,
  MicropaymentCreate,
  MicropaymentRead,
  MicropaymentValueConfigRequest,
  MicropaymentValueConfigResponse,
} from "../../model";
import type { ErrorType, BodyType } from "../../mutator/axios-instance-v3";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Synchronize micropayment configurations for a given study and country.
 *
 * This endpoint receives a request payload containing the study and country identifiers,
 * along with the desired micropayment configuration data. It updates or creates the
 * micropayment configuration in the database accordingly.
 *
 * Args:
 *     payload (MicropaymentConfigurationSyncRequest): The request body containing study, country, and configuration data.
 *     session (SessionDep): Database session dependency.
 *
 * Returns:
 *     DataResponse[list[MicropaymentConfigurationInfo]]: The result of the synchronization, including the updated configuration.
 *
 * Raises:
 *     NotFoundError: If the specified study does not exist or the country is not part of the study.
 * @summary Sync Micropayment Configurations
 */
export const syncMicropaymentConfigurationsMicropaymentsConfigPost = (
  micropaymentConfigurationSyncRequest: BodyType<MicropaymentConfigurationSyncRequest>,
  options?: SecondParameter<typeof nmibleInstanceV3>,
  signal?: AbortSignal
) => {
  return nmibleInstanceV3<DataResponseListMicropaymentConfigurationInfo>(
    {
      url: `/micropayments/config`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: micropaymentConfigurationSyncRequest,
      signal,
    },
    options
  );
};

export const getSyncMicropaymentConfigurationsMicropaymentsConfigPostMutationKey =
  () => ["syncMicropaymentConfigurationsMicropaymentsConfigPost"] as const;

export const getSyncMicropaymentConfigurationsMicropaymentsConfigPostMutationOptions =
  <
    TError = ErrorType<ErrorResponse | HTTPValidationError>,
    TContext = unknown,
  >(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof syncMicropaymentConfigurationsMicropaymentsConfigPost>
      >,
      TError,
      SyncMicropaymentConfigurationsMicropaymentsConfigPostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof syncMicropaymentConfigurationsMicropaymentsConfigPost>
    >,
    TError,
    SyncMicropaymentConfigurationsMicropaymentsConfigPostMutationVariables,
    TContext
  > => {
    const mutationKey =
      getSyncMicropaymentConfigurationsMicropaymentsConfigPostMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
      ? options.mutation &&
        "mutationKey" in options.mutation &&
        options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof syncMicropaymentConfigurationsMicropaymentsConfigPost>
      >,
      SyncMicropaymentConfigurationsMicropaymentsConfigPostMutationVariables
    > = (props) => {
      const { data } = props ?? {};

      return syncMicropaymentConfigurationsMicropaymentsConfigPost(
        data,
        requestOptions
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type SyncMicropaymentConfigurationsMicropaymentsConfigPostMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof syncMicropaymentConfigurationsMicropaymentsConfigPost>
    >
  >;
export type SyncMicropaymentConfigurationsMicropaymentsConfigPostMutationBody =
  BodyType<MicropaymentConfigurationSyncRequest>;
export type SyncMicropaymentConfigurationsMicropaymentsConfigPostMutationError =
  ErrorType<ErrorResponse | HTTPValidationError>;
export interface SyncMicropaymentConfigurationsMicropaymentsConfigPostMutationVariables {
  data: BodyType<MicropaymentConfigurationSyncRequest>;
}

/**
 * @summary Sync Micropayment Configurations
 */
export const useSyncMicropaymentConfigurationsMicropaymentsConfigPost = <
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof syncMicropaymentConfigurationsMicropaymentsConfigPost>
      >,
      TError,
      SyncMicropaymentConfigurationsMicropaymentsConfigPostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<
    ReturnType<typeof syncMicropaymentConfigurationsMicropaymentsConfigPost>
  >,
  TError,
  SyncMicropaymentConfigurationsMicropaymentsConfigPostMutationVariables,
  TContext
> => {
  return useMutation(
    getSyncMicropaymentConfigurationsMicropaymentsConfigPostMutationOptions(
      options
    ),
    queryClient
  );
};
/**
 * Retrieve the micropayment configuration for a given study.
 *
 * This endpoint fetches the micropayment configuration associated with the specified study.
 * If no study_id is provided, it may return a default or raise an error depending on implementation.
 *
 * Args:
 *     study_id (Optional[UUID]): The unique identifier of the study to retrieve the configuration for.
 *     session (SessionDep, optional): Database session dependency.
 *
 * Returns:
 *     DataResponse[list[MicropaymentConfigurationInfo]]: The micropayment configuration for the specified study.
 *
 * Raises:
 *     NotFoundError: If the specified study does not exist.
 * @summary Get Micropayment Configuration
 */
export const getMicropaymentConfigurationMicropaymentsConfigGet = (
  params: GetMicropaymentConfigurationMicropaymentsConfigGetParams,
  options?: SecondParameter<typeof nmibleInstanceV3>,
  signal?: AbortSignal
) => {
  return nmibleInstanceV3<DataResponseListMicropaymentConfigurationInfo>(
    { url: `/micropayments/config`, method: "GET", params, signal },
    options
  );
};

export const getGetMicropaymentConfigurationMicropaymentsConfigGetQueryKey = (
  params?: GetMicropaymentConfigurationMicropaymentsConfigGetParams
) => {
  return [`/micropayments/config`, ...(params ? [params] : [])] as const;
};

export const getGetMicropaymentConfigurationMicropaymentsConfigGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<typeof getMicropaymentConfigurationMicropaymentsConfigGet>
    >,
    TError = ErrorType<ErrorResponse | HTTPValidationError>,
  >(
    params: GetMicropaymentConfigurationMicropaymentsConfigGetParams,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof getMicropaymentConfigurationMicropaymentsConfigGet
            >
          >,
          TError,
          TData
        >
      >;
      request?: SecondParameter<typeof nmibleInstanceV3>;
    }
  ) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetMicropaymentConfigurationMicropaymentsConfigGetQueryKey(params);

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<typeof getMicropaymentConfigurationMicropaymentsConfigGet>
      >
    > = ({ signal }) =>
      getMicropaymentConfigurationMicropaymentsConfigGet(
        params,
        requestOptions,
        signal
      );

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
      Awaited<
        ReturnType<typeof getMicropaymentConfigurationMicropaymentsConfigGet>
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type GetMicropaymentConfigurationMicropaymentsConfigGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<typeof getMicropaymentConfigurationMicropaymentsConfigGet>
    >
  >;
export type GetMicropaymentConfigurationMicropaymentsConfigGetQueryError =
  ErrorType<ErrorResponse | HTTPValidationError>;

export function useGetMicropaymentConfigurationMicropaymentsConfigGet<
  TData = Awaited<
    ReturnType<typeof getMicropaymentConfigurationMicropaymentsConfigGet>
  >,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  params: GetMicropaymentConfigurationMicropaymentsConfigGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getMicropaymentConfigurationMicropaymentsConfigGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getMicropaymentConfigurationMicropaymentsConfigGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getMicropaymentConfigurationMicropaymentsConfigGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetMicropaymentConfigurationMicropaymentsConfigGet<
  TData = Awaited<
    ReturnType<typeof getMicropaymentConfigurationMicropaymentsConfigGet>
  >,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  params: GetMicropaymentConfigurationMicropaymentsConfigGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getMicropaymentConfigurationMicropaymentsConfigGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getMicropaymentConfigurationMicropaymentsConfigGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getMicropaymentConfigurationMicropaymentsConfigGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetMicropaymentConfigurationMicropaymentsConfigGet<
  TData = Awaited<
    ReturnType<typeof getMicropaymentConfigurationMicropaymentsConfigGet>
  >,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  params: GetMicropaymentConfigurationMicropaymentsConfigGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getMicropaymentConfigurationMicropaymentsConfigGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Micropayment Configuration
 */

export function useGetMicropaymentConfigurationMicropaymentsConfigGet<
  TData = Awaited<
    ReturnType<typeof getMicropaymentConfigurationMicropaymentsConfigGet>
  >,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  params: GetMicropaymentConfigurationMicropaymentsConfigGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getMicropaymentConfigurationMicropaymentsConfigGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetMicropaymentConfigurationMicropaymentsConfigGetQueryOptions(
      params,
      options
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Update the date on an existing micropayment confirmation.
 *
 * Raises:
 *     NotFoundError: If no confirmation exists for this
 *         participant and configuration combination.
 *     MicropaymentConfirmationDateInvalidError: If the new date is in the future.
 * @summary Update Micropayment Confirmation
 */
export const updateMicropaymentConfirmationMicropaymentsConfigConfirmPut = (
  micropaymentConfirmationRequest: BodyType<MicropaymentConfirmationRequest>,
  options?: SecondParameter<typeof nmibleInstanceV3>,
  signal?: AbortSignal
) => {
  return nmibleInstanceV3<DataResponseMicropaymentConfirmationRead>(
    {
      url: `/micropayments/config/confirm`,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data: micropaymentConfirmationRequest,
      signal,
    },
    options
  );
};

export const getUpdateMicropaymentConfirmationMicropaymentsConfigConfirmPutMutationKey =
  () =>
    ["updateMicropaymentConfirmationMicropaymentsConfigConfirmPut"] as const;

export const getUpdateMicropaymentConfirmationMicropaymentsConfigConfirmPutMutationOptions =
  <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof updateMicropaymentConfirmationMicropaymentsConfigConfirmPut
        >
      >,
      TError,
      UpdateMicropaymentConfirmationMicropaymentsConfigConfirmPutMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<
        typeof updateMicropaymentConfirmationMicropaymentsConfigConfirmPut
      >
    >,
    TError,
    UpdateMicropaymentConfirmationMicropaymentsConfigConfirmPutMutationVariables,
    TContext
  > => {
    const mutationKey =
      getUpdateMicropaymentConfirmationMicropaymentsConfigConfirmPutMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
      ? options.mutation &&
        "mutationKey" in options.mutation &&
        options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof updateMicropaymentConfirmationMicropaymentsConfigConfirmPut
        >
      >,
      UpdateMicropaymentConfirmationMicropaymentsConfigConfirmPutMutationVariables
    > = (props) => {
      const { data } = props ?? {};

      return updateMicropaymentConfirmationMicropaymentsConfigConfirmPut(
        data,
        requestOptions
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type UpdateMicropaymentConfirmationMicropaymentsConfigConfirmPutMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof updateMicropaymentConfirmationMicropaymentsConfigConfirmPut
      >
    >
  >;
export type UpdateMicropaymentConfirmationMicropaymentsConfigConfirmPutMutationBody =
  BodyType<MicropaymentConfirmationRequest>;
export type UpdateMicropaymentConfirmationMicropaymentsConfigConfirmPutMutationError =
  ErrorType<ErrorResponse>;
export interface UpdateMicropaymentConfirmationMicropaymentsConfigConfirmPutMutationVariables {
  data: BodyType<MicropaymentConfirmationRequest>;
}

/**
 * @summary Update Micropayment Confirmation
 */
export const useUpdateMicropaymentConfirmationMicropaymentsConfigConfirmPut = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof updateMicropaymentConfirmationMicropaymentsConfigConfirmPut
        >
      >,
      TError,
      UpdateMicropaymentConfirmationMicropaymentsConfigConfirmPutMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<
    ReturnType<
      typeof updateMicropaymentConfirmationMicropaymentsConfigConfirmPut
    >
  >,
  TError,
  UpdateMicropaymentConfirmationMicropaymentsConfigConfirmPutMutationVariables,
  TContext
> => {
  return useMutation(
    getUpdateMicropaymentConfirmationMicropaymentsConfigConfirmPutMutationOptions(
      options
    ),
    queryClient
  );
};
/**
 * Create a confirmation record for a participant's micropayment configuration.
 *
 * Raises:
 *     NotFoundError: If the micropayment configuration or participant does not exist.
 *     MicropaymentConfirmationAlreadyExistsError: If a confirmation already exists for
 *         this participant and configuration combination.
 *     MicropaymentConfirmationDateInvalidError: If the confirmation date is in the future.
 * @summary Create Micropayment Confirmation
 */
export const createMicropaymentConfirmationMicropaymentsConfigConfirmPost = (
  micropaymentConfirmationRequest: BodyType<MicropaymentConfirmationRequest>,
  options?: SecondParameter<typeof nmibleInstanceV3>,
  signal?: AbortSignal
) => {
  return nmibleInstanceV3<DataResponseMicropaymentConfirmationRead>(
    {
      url: `/micropayments/config/confirm`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: micropaymentConfirmationRequest,
      signal,
    },
    options
  );
};

export const getCreateMicropaymentConfirmationMicropaymentsConfigConfirmPostMutationKey =
  () =>
    ["createMicropaymentConfirmationMicropaymentsConfigConfirmPost"] as const;

export const getCreateMicropaymentConfirmationMicropaymentsConfigConfirmPostMutationOptions =
  <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof createMicropaymentConfirmationMicropaymentsConfigConfirmPost
        >
      >,
      TError,
      CreateMicropaymentConfirmationMicropaymentsConfigConfirmPostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<
        typeof createMicropaymentConfirmationMicropaymentsConfigConfirmPost
      >
    >,
    TError,
    CreateMicropaymentConfirmationMicropaymentsConfigConfirmPostMutationVariables,
    TContext
  > => {
    const mutationKey =
      getCreateMicropaymentConfirmationMicropaymentsConfigConfirmPostMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
      ? options.mutation &&
        "mutationKey" in options.mutation &&
        options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof createMicropaymentConfirmationMicropaymentsConfigConfirmPost
        >
      >,
      CreateMicropaymentConfirmationMicropaymentsConfigConfirmPostMutationVariables
    > = (props) => {
      const { data } = props ?? {};

      return createMicropaymentConfirmationMicropaymentsConfigConfirmPost(
        data,
        requestOptions
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type CreateMicropaymentConfirmationMicropaymentsConfigConfirmPostMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof createMicropaymentConfirmationMicropaymentsConfigConfirmPost
      >
    >
  >;
export type CreateMicropaymentConfirmationMicropaymentsConfigConfirmPostMutationBody =
  BodyType<MicropaymentConfirmationRequest>;
export type CreateMicropaymentConfirmationMicropaymentsConfigConfirmPostMutationError =
  ErrorType<ErrorResponse>;
export interface CreateMicropaymentConfirmationMicropaymentsConfigConfirmPostMutationVariables {
  data: BodyType<MicropaymentConfirmationRequest>;
}

/**
 * @summary Create Micropayment Confirmation
 */
export const useCreateMicropaymentConfirmationMicropaymentsConfigConfirmPost = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof createMicropaymentConfirmationMicropaymentsConfigConfirmPost
        >
      >,
      TError,
      CreateMicropaymentConfirmationMicropaymentsConfigConfirmPostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<
    ReturnType<
      typeof createMicropaymentConfirmationMicropaymentsConfigConfirmPost
    >
  >,
  TError,
  CreateMicropaymentConfirmationMicropaymentsConfigConfirmPostMutationVariables,
  TContext
> => {
  return useMutation(
    getCreateMicropaymentConfirmationMicropaymentsConfigConfirmPostMutationOptions(
      options
    ),
    queryClient
  );
};
/**
 * [Admin Only] Retrieve micropayment configurations for a participant.
 *
 * This endpoint returns:
 * - All configurations that are not deleted
 *
 * For each configuration, the response includes:
 * - The associated sent micropayments
 * - The applicable micropayment value for each micropayment
 *
 * Args:
 *     participant_id (UUID): The unique identifier of the participant.
 *     session (Session): Database session dependency.
 *
 * Returns:
 *     DataResponse[list[MicropaymentConfigurationWithMicropayments]]: List of micropayment configurations
 *     with their sent micropayments and values.
 *
 * Raises:
 *     AuthForbiddenError: If the user is not an admin.
 *     NotFoundError: If the specified participant does not exist.
 * @summary Get Participant Micropayment Configurations
 */
export const getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet =
  (
    participantId: string,
    options?: SecondParameter<typeof nmibleInstanceV3>,
    signal?: AbortSignal
  ) => {
    return nmibleInstanceV3<DataResponseListMicropaymentConfigurationWithMicropayments>(
      {
        url: `/micropayments/config/participant/${participantId}`,
        method: "GET",
        signal,
      },
      options
    );
  };

export const getGetParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGetQueryKey =
  (participantId: string) => {
    return [`/micropayments/config/participant/${participantId}`] as const;
  };

export const getGetParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
      >
    >,
    TError = ErrorType<ErrorResponse | HTTPValidationError>,
  >(
    participantId: string,
    options?: {
      query?: Partial<
        UseQueryOptions<
          Awaited<
            ReturnType<
              typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
            >
          >,
          TError,
          TData
        >
      >;
      request?: SecondParameter<typeof nmibleInstanceV3>;
    }
  ) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGetQueryKey(
        participantId
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
        >
      >
    > = ({ signal }) =>
      getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet(
        participantId,
        requestOptions,
        signal
      );

    return {
      queryKey,
      queryFn,
      enabled: participantId !== null && participantId !== undefined,
      ...queryOptions,
    } as UseQueryOptions<
      Awaited<
        ReturnType<
          typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
        >
      >,
      TError,
      TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
  };

export type GetParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
      >
    >
  >;
export type GetParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGetQueryError =
  ErrorType<ErrorResponse | HTTPValidationError>;

export function useGetParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet<
  TData = Awaited<
    ReturnType<
      typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
    >
  >,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  participantId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet<
  TData = Awaited<
    ReturnType<
      typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
    >
  >,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  participantId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
            >
          >,
          TError,
          Awaited<
            ReturnType<
              typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
            >
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet<
  TData = Awaited<
    ReturnType<
      typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
    >
  >,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  participantId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Participant Micropayment Configurations
 */

export function useGetParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet<
  TData = Awaited<
    ReturnType<
      typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
    >
  >,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  participantId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGet
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetParticipantMicropaymentConfigurationsMicropaymentsConfigParticipantParticipantIdGetQueryOptions(
      participantId,
      options
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Get micropayment value configuration for a given scope.
 *
 * If scope is Site, also fetches values for the parent Study Country scope
 * to handle inheritance/defaults.
 * @summary Get Micropayment Value Config
 */
export const getMicropaymentValueConfigMicropaymentsValuesGet = (
  params?: GetMicropaymentValueConfigMicropaymentsValuesGetParams,
  options?: SecondParameter<typeof nmibleInstanceV3>,
  signal?: AbortSignal
) => {
  return nmibleInstanceV3<MicropaymentValueConfigResponse>(
    { url: `/micropayments/values`, method: "GET", params, signal },
    options
  );
};

export const getGetMicropaymentValueConfigMicropaymentsValuesGetQueryKey = (
  params?: GetMicropaymentValueConfigMicropaymentsValuesGetParams
) => {
  return [`/micropayments/values`, ...(params ? [params] : [])] as const;
};

export const getGetMicropaymentValueConfigMicropaymentsValuesGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
  >,
  TError = ErrorType<ErrorResponse>,
>(
  params?: GetMicropaymentValueConfigMicropaymentsValuesGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetMicropaymentValueConfigMicropaymentsValuesGetQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>>
  > = ({ signal }) =>
    getMicropaymentValueConfigMicropaymentsValuesGet(
      params,
      requestOptions,
      signal
    );

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<
      ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetMicropaymentValueConfigMicropaymentsValuesGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>>
  >;
export type GetMicropaymentValueConfigMicropaymentsValuesGetQueryError =
  ErrorType<ErrorResponse>;

export function useGetMicropaymentValueConfigMicropaymentsValuesGet<
  TData = Awaited<
    ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
  >,
  TError = ErrorType<ErrorResponse>,
>(
  params: undefined | GetMicropaymentValueConfigMicropaymentsValuesGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetMicropaymentValueConfigMicropaymentsValuesGet<
  TData = Awaited<
    ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
  >,
  TError = ErrorType<ErrorResponse>,
>(
  params?: GetMicropaymentValueConfigMicropaymentsValuesGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetMicropaymentValueConfigMicropaymentsValuesGet<
  TData = Awaited<
    ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
  >,
  TError = ErrorType<ErrorResponse>,
>(
  params?: GetMicropaymentValueConfigMicropaymentsValuesGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Micropayment Value Config
 */

export function useGetMicropaymentValueConfigMicropaymentsValuesGet<
  TData = Awaited<
    ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
  >,
  TError = ErrorType<ErrorResponse>,
>(
  params?: GetMicropaymentValueConfigMicropaymentsValuesGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getMicropaymentValueConfigMicropaymentsValuesGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetMicropaymentValueConfigMicropaymentsValuesGetQueryOptions(
      params,
      options
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Bulk update micropayment values for a given scope.
 *
 * Site-level values will override Study Country-level values.
 * @summary Configure Micropayment Values
 */
export const configureMicropaymentValuesMicropaymentsValuesPost = (
  micropaymentValueConfigRequest: BodyType<MicropaymentValueConfigRequest>,
  options?: SecondParameter<typeof nmibleInstanceV3>,
  signal?: AbortSignal
) => {
  return nmibleInstanceV3<MicropaymentValueConfigResponse>(
    {
      url: `/micropayments/values`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: micropaymentValueConfigRequest,
      signal,
    },
    options
  );
};

export const getConfigureMicropaymentValuesMicropaymentsValuesPostMutationKey =
  () => ["configureMicropaymentValuesMicropaymentsValuesPost"] as const;

export const getConfigureMicropaymentValuesMicropaymentsValuesPostMutationOptions =
  <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof configureMicropaymentValuesMicropaymentsValuesPost>
      >,
      TError,
      ConfigureMicropaymentValuesMicropaymentsValuesPostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof configureMicropaymentValuesMicropaymentsValuesPost>
    >,
    TError,
    ConfigureMicropaymentValuesMicropaymentsValuesPostMutationVariables,
    TContext
  > => {
    const mutationKey =
      getConfigureMicropaymentValuesMicropaymentsValuesPostMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
      ? options.mutation &&
        "mutationKey" in options.mutation &&
        options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof configureMicropaymentValuesMicropaymentsValuesPost>
      >,
      ConfigureMicropaymentValuesMicropaymentsValuesPostMutationVariables
    > = (props) => {
      const { data } = props ?? {};

      return configureMicropaymentValuesMicropaymentsValuesPost(
        data,
        requestOptions
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type ConfigureMicropaymentValuesMicropaymentsValuesPostMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof configureMicropaymentValuesMicropaymentsValuesPost>
    >
  >;
export type ConfigureMicropaymentValuesMicropaymentsValuesPostMutationBody =
  BodyType<MicropaymentValueConfigRequest>;
export type ConfigureMicropaymentValuesMicropaymentsValuesPostMutationError =
  ErrorType<ErrorResponse>;
export interface ConfigureMicropaymentValuesMicropaymentsValuesPostMutationVariables {
  data: BodyType<MicropaymentValueConfigRequest>;
}

/**
 * @summary Configure Micropayment Values
 */
export const useConfigureMicropaymentValuesMicropaymentsValuesPost = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof configureMicropaymentValuesMicropaymentsValuesPost>
      >,
      TError,
      ConfigureMicropaymentValuesMicropaymentsValuesPostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<
    ReturnType<typeof configureMicropaymentValuesMicropaymentsValuesPost>
  >,
  TError,
  ConfigureMicropaymentValuesMicropaymentsValuesPostMutationVariables,
  TContext
> => {
  return useMutation(
    getConfigureMicropaymentValuesMicropaymentsValuesPostMutationOptions(
      options
    ),
    queryClient
  );
};
/**
 * @summary Create Micropayment
 */
export const createMicropaymentMicropaymentsPost = (
  micropaymentCreate: BodyType<MicropaymentCreate>,
  options?: SecondParameter<typeof nmibleInstanceV3>,
  signal?: AbortSignal
) => {
  return nmibleInstanceV3<MicropaymentRead>(
    {
      url: `/micropayments`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: micropaymentCreate,
      signal,
    },
    options
  );
};

export const getCreateMicropaymentMicropaymentsPostMutationKey = () =>
  ["createMicropaymentMicropaymentsPost"] as const;

export const getCreateMicropaymentMicropaymentsPostMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMicropaymentMicropaymentsPost>>,
    TError,
    CreateMicropaymentMicropaymentsPostMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof nmibleInstanceV3>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createMicropaymentMicropaymentsPost>>,
  TError,
  CreateMicropaymentMicropaymentsPostMutationVariables,
  TContext
> => {
  const mutationKey = getCreateMicropaymentMicropaymentsPostMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createMicropaymentMicropaymentsPost>>,
    CreateMicropaymentMicropaymentsPostMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return createMicropaymentMicropaymentsPost(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateMicropaymentMicropaymentsPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof createMicropaymentMicropaymentsPost>>
>;
export type CreateMicropaymentMicropaymentsPostMutationBody =
  BodyType<MicropaymentCreate>;
export type CreateMicropaymentMicropaymentsPostMutationError =
  ErrorType<ErrorResponse>;
export interface CreateMicropaymentMicropaymentsPostMutationVariables {
  data: BodyType<MicropaymentCreate>;
}

/**
 * @summary Create Micropayment
 */
export const useCreateMicropaymentMicropaymentsPost = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createMicropaymentMicropaymentsPost>>,
      TError,
      CreateMicropaymentMicropaymentsPostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createMicropaymentMicropaymentsPost>>,
  TError,
  CreateMicropaymentMicropaymentsPostMutationVariables,
  TContext
> => {
  return useMutation(
    getCreateMicropaymentMicropaymentsPostMutationOptions(options),
    queryClient
  );
};
/**
 * A participant gets `created_by_user` and `internal_notes` as null.
 * @summary Get Micropayment
 */
export const getMicropaymentMicropaymentsIdGet = (
  id: string,
  options?: SecondParameter<typeof nmibleInstanceV3>,
  signal?: AbortSignal
) => {
  return nmibleInstanceV3<MicropaymentRead>(
    { url: `/micropayments/${id}`, method: "GET", signal },
    options
  );
};

export const getGetMicropaymentMicropaymentsIdGetQueryKey = (id: string) => {
  return [`/micropayments/${id}`] as const;
};

export const getGetMicropaymentMicropaymentsIdGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetMicropaymentMicropaymentsIdGetQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>
  > = ({ signal }) =>
    getMicropaymentMicropaymentsIdGet(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetMicropaymentMicropaymentsIdGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>
>;
export type GetMicropaymentMicropaymentsIdGetQueryError = ErrorType<
  ErrorResponse | HTTPValidationError
>;

export function useGetMicropaymentMicropaymentsIdGet<
  TData = Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>,
          TError,
          Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetMicropaymentMicropaymentsIdGet<
  TData = Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>,
          TError,
          Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetMicropaymentMicropaymentsIdGet<
  TData = Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Micropayment
 */

export function useGetMicropaymentMicropaymentsIdGet<
  TData = Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getMicropaymentMicropaymentsIdGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetMicropaymentMicropaymentsIdGetQueryOptions(
    id,
    options
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Cancel Micropayment
 */
export const cancelMicropaymentMicropaymentsIdDelete = (
  id: string,
  options?: SecondParameter<typeof nmibleInstanceV3>,
  signal?: AbortSignal
) => {
  return nmibleInstanceV3<unknown>(
    { url: `/micropayments/${id}`, method: "DELETE", signal },
    options
  );
};

export const getCancelMicropaymentMicropaymentsIdDeleteMutationKey = () =>
  ["cancelMicropaymentMicropaymentsIdDelete"] as const;

export const getCancelMicropaymentMicropaymentsIdDeleteMutationOptions = <
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelMicropaymentMicropaymentsIdDelete>>,
    TError,
    CancelMicropaymentMicropaymentsIdDeleteMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof nmibleInstanceV3>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof cancelMicropaymentMicropaymentsIdDelete>>,
  TError,
  CancelMicropaymentMicropaymentsIdDeleteMutationVariables,
  TContext
> => {
  const mutationKey = getCancelMicropaymentMicropaymentsIdDeleteMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof cancelMicropaymentMicropaymentsIdDelete>>,
    CancelMicropaymentMicropaymentsIdDeleteMutationVariables
  > = (props) => {
    const { id } = props ?? {};

    return cancelMicropaymentMicropaymentsIdDelete(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CancelMicropaymentMicropaymentsIdDeleteMutationResult = NonNullable<
  Awaited<ReturnType<typeof cancelMicropaymentMicropaymentsIdDelete>>
>;

export type CancelMicropaymentMicropaymentsIdDeleteMutationError = ErrorType<
  ErrorResponse | HTTPValidationError
>;
export interface CancelMicropaymentMicropaymentsIdDeleteMutationVariables {
  id: string;
}

/**
 * @summary Cancel Micropayment
 */
export const useCancelMicropaymentMicropaymentsIdDelete = <
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof cancelMicropaymentMicropaymentsIdDelete>>,
      TError,
      CancelMicropaymentMicropaymentsIdDeleteMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof cancelMicropaymentMicropaymentsIdDelete>>,
  TError,
  CancelMicropaymentMicropaymentsIdDeleteMutationVariables,
  TContext
> => {
  return useMutation(
    getCancelMicropaymentMicropaymentsIdDeleteMutationOptions(options),
    queryClient
  );
};
/**
 * @summary Action Micropayment
 */
export const actionMicropaymentMicropaymentsIdActionPost = (
  id: string,
  micropaymentAction: BodyType<MicropaymentAction>,
  options?: SecondParameter<typeof nmibleInstanceV3>,
  signal?: AbortSignal
) => {
  return nmibleInstanceV3<MicropaymentRead>(
    {
      url: `/micropayments/${id}/action`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: micropaymentAction,
      signal,
    },
    options
  );
};

export const getActionMicropaymentMicropaymentsIdActionPostMutationKey = () =>
  ["actionMicropaymentMicropaymentsIdActionPost"] as const;

export const getActionMicropaymentMicropaymentsIdActionPostMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof actionMicropaymentMicropaymentsIdActionPost>>,
    TError,
    ActionMicropaymentMicropaymentsIdActionPostMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof nmibleInstanceV3>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof actionMicropaymentMicropaymentsIdActionPost>>,
  TError,
  ActionMicropaymentMicropaymentsIdActionPostMutationVariables,
  TContext
> => {
  const mutationKey =
    getActionMicropaymentMicropaymentsIdActionPostMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof actionMicropaymentMicropaymentsIdActionPost>>,
    ActionMicropaymentMicropaymentsIdActionPostMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return actionMicropaymentMicropaymentsIdActionPost(
      id,
      data,
      requestOptions
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type ActionMicropaymentMicropaymentsIdActionPostMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof actionMicropaymentMicropaymentsIdActionPost>>
  >;
export type ActionMicropaymentMicropaymentsIdActionPostMutationBody =
  BodyType<MicropaymentAction>;
export type ActionMicropaymentMicropaymentsIdActionPostMutationError =
  ErrorType<ErrorResponse>;
export interface ActionMicropaymentMicropaymentsIdActionPostMutationVariables {
  id: string;
  data: BodyType<MicropaymentAction>;
}

/**
 * @summary Action Micropayment
 */
export const useActionMicropaymentMicropaymentsIdActionPost = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof actionMicropaymentMicropaymentsIdActionPost>>,
      TError,
      ActionMicropaymentMicropaymentsIdActionPostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof nmibleInstanceV3>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof actionMicropaymentMicropaymentsIdActionPost>>,
  TError,
  ActionMicropaymentMicropaymentsIdActionPostMutationVariables,
  TContext
> => {
  return useMutation(
    getActionMicropaymentMicropaymentsIdActionPostMutationOptions(options),
    queryClient
  );
};
